import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { 
  Settings, 
  Save, 
  Loader2, 
  HelpCircle,
  Clock,
  MessageSquare,
  Volume2,
  Shield,
  HardDrive,
  CheckCircle2,
  XCircle,
  Link,
  AlertTriangle,
  Eye,
  Users,
  UserX,
  RefreshCw,
  Coins,
  PoundSterling,
  Mail,
  Send,
  Server,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [driveStatus, setDriveStatus] = useState({ connected: false, checking: true });
  const [connectingDrive, setConnectingDrive] = useState(false);
  const [creditSummary, setCreditSummary] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [resettingCredits, setResettingCredits] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [questionCategories, setQuestionCategories] = useState([]);
  const [categoryPercentages, setCategoryPercentages] = useState({});
  const [settings, setSettings] = useState({
    max_interview_questions: 10,
    total_interview_time: 10,
    default_listening_time: 30,
    default_answering_time: 90,
    passing_percentage: 50,
    answer_relevancy_threshold: 70,
    category_percentages: null,
    enable_ai_assessment: true,
    enable_tts_questions: true,
    tts_voice: 'female',
    enable_face_verification: true,
    enable_liveness_check: true,
    face_match_threshold: 60,
    enable_integrity_check: true,
    enable_recording: true,
    recording_quality: 'high',
    // Failing Criteria
    fail_on_low_eye_contact: true,
    eye_contact_threshold: 40,
    fail_on_low_keyword_match: true,
    keyword_match_threshold: 30,
    fail_on_dual_face: true,
    fail_on_face_absent: true,
    fail_on_face_swap: true,
    face_swap_threshold: 50,
    // Credit Pricing
    student_price_per_credit: 15,
    student_min_credits: 1,
    agent_price_per_credit: 12,
    agent_min_credits: 10,
    organization_price_per_credit: 10,
    organization_min_credits: 100,
    credit_currency: 'GBP',
    // SMTP Settings
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'VisaInterviews.ai',
    smtp_use_tls: true
  });
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchCategories();
    checkDriveStatus();
    
    // Check for drive connection status from URL params
    if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive connected successfully!');
      setDriveStatus({ connected: true, checking: false });
    } else if (searchParams.get('drive_error')) {
      toast.error(`Google Drive connection failed: ${searchParams.get('drive_error')}`);
    }
  }, [searchParams]);

  const checkDriveStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/oauth/drive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriveStatus({ connected: response.data.connected, checking: false });
    } catch (error) {
      setDriveStatus({ connected: false, checking: false });
    }
  };

  const connectGoogleDrive = async () => {
    setConnectingDrive(true);
    try {
      const response = await axios.get(`${API_URL}/api/oauth/drive/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Google Drive connection');
      setConnectingDrive(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
        if (response.data.category_percentages) {
          setCategoryPercentages(response.data.category_percentages);
        }
      }
    } catch (error) {
      console.log('No settings found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Include category percentages in settings
      const settingsToSave = { ...settings, category_percentages: categoryPercentages };
      await axios.post(`${API_URL}/api/admin/settings`, settingsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/questions/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rawCats = response.data?.categories || response.data || [];
      const cats = rawCats.filter(c => c && c !== 'Uncategorized');
      setQuestionCategories(cats);
      
      // Initialize percentages from settings or distribute evenly
      const savedPercentages = settings.category_percentages;
      if (savedPercentages && Object.keys(savedPercentages).length > 0) {
        setCategoryPercentages(savedPercentages);
      } else if (cats.length > 0) {
        const even = Math.floor(100 / cats.length);
        const remainder = 100 - (even * cats.length);
        const initial = {};
        cats.forEach((c, i) => { initial[c] = even + (i === 0 ? remainder : 0); });
        setCategoryPercentages(initial);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const testSmtpConnection = async () => {
    setTestingSmtp(true);
    try {
      // First save the settings
      await axios.post(`${API_URL}/api/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then test the connection
      const response = await axios.post(`${API_URL}/api/admin/settings/test-smtp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('SMTP connection successful!');
      } else {
        toast.error(response.data.message || 'SMTP connection failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to test SMTP connection');
    } finally {
      setTestingSmtp(false);
    }
  };

  const sendTestEmailFunc = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setTestingSmtp(true);
    try {
      // First save the settings
      await axios.post(`${API_URL}/api/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then send test email
      const response = await axios.post(`${API_URL}/api/admin/settings/send-test-email?email=${encodeURIComponent(testEmail)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Test email sent to ${testEmail}!`);
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setTestingSmtp(false);
    }
  };

  // Fetch credit summary for all organizations
  const fetchCreditSummary = async () => {
    setLoadingCredits(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/credit-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditSummary(response.data);
    } catch (error) {
      toast.error('Failed to fetch credit summary');
    } finally {
      setLoadingCredits(false);
    }
  };

  // Reset all credits across the platform
  const resetAllCredits = async () => {
    setResettingCredits(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/reset-all-credits`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`All credits reset! ${response.data.stats.organizations_reset} organizations, ${response.data.stats.agents_reset} agents affected.`);
        setShowResetConfirm(false);
        setCreditSummary(null);
        // Refresh summary
        fetchCreditSummary();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset credits');
    } finally {
      setResettingCredits(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="super_admin">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Settings className="w-8 h-8 text-lime-500" />
              Interview Settings
            </h1>
            <p className="text-muted-foreground mt-1">Configure interview parameters and behavior</p>
          </div>
          <Button 
            onClick={saveSettings} 
            disabled={saving}
            className="bg-lime-500 hover:bg-lime-600"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Google Drive Integration */}
          <Card className="bg-card border-border lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-blue-500" />
                Google Drive Integration
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Connect your Google Drive to store interview recordings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center gap-4">
                  {driveStatus.checking ? (
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  ) : driveStatus.connected ? (
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-yellow-500" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-foreground font-semibold">
                      {driveStatus.checking 
                        ? 'Checking connection...' 
                        : driveStatus.connected 
                          ? 'Google Drive Connected' 
                          : 'Google Drive Not Connected'}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {driveStatus.connected 
                        ? 'Interview recordings will be automatically saved to your Google Drive'
                        : 'Connect your Google Drive to enable video recording storage'}
                    </p>
                  </div>
                </div>
                {!driveStatus.connected && !driveStatus.checking && (
                  <Button
                    onClick={connectGoogleDrive}
                    disabled={connectingDrive}
                    className="bg-blue-500 hover:bg-blue-600 text-foreground"
                  >
                    {connectingDrive ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Link className="w-4 h-4 mr-2" />
                    )}
                    Connect Google Drive
                  </Button>
                )}
                {driveStatus.connected && (
                  <div className="flex items-center gap-2 text-green-500">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Active</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Interview Questions Settings */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-500" />
                Question Settings
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure how questions are displayed during interviews
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground">Maximum Questions per Interview</Label>
                  <span className="text-2xl font-bold text-lime-500">{settings.max_interview_questions}</span>
                </div>
                <Slider
                  value={[settings.max_interview_questions]}
                  onValueChange={(v) => setSettings({ ...settings, max_interview_questions: v[0] })}
                  min={1}
                  max={50}
                  step={1}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Questions will be randomly selected from the question bank. Set to limit interview length.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground">Total Interview Time (minutes)</Label>
                  <span className="text-2xl font-bold text-blue-500">{settings.total_interview_time} min</span>
                </div>
                <Slider
                  value={[settings.total_interview_time]}
                  onValueChange={(v) => setSettings({ ...settings, total_interview_time: v[0] })}
                  min={5}
                  max={60}
                  step={1}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum total duration for the entire interview. Interview will auto-complete when time runs out.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground">Answer Relevancy Threshold (%)</Label>
                  <span className="text-2xl font-bold text-orange-500">{settings.answer_relevancy_threshold}%</span>
                </div>
                <Slider
                  value={[settings.answer_relevancy_threshold]}
                  onValueChange={(v) => setSettings({ ...settings, answer_relevancy_threshold: v[0] })}
                  min={10}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Minimum relevancy score for an answer to be considered acceptable. AI evaluates against question keywords and answer guidance.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Default Reading Time (sec)</Label>
                  <Input
                    type="number"
                    value={settings.default_listening_time}
                    onChange={(e) => setSettings({ ...settings, default_listening_time: parseInt(e.target.value) || 30 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Default Answer Time (sec)</Label>
                  <Input
                    type="number"
                    value={settings.default_answering_time}
                    onChange={(e) => setSettings({ ...settings, default_answering_time: parseInt(e.target.value) || 90 })}
                  />
                </div>
              </div>

              {/* Passing Percentage Setting */}
              <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground">Passing Percentage</Label>
                  <span className="text-2xl font-bold text-green-500">{settings.passing_percentage}%</span>
                </div>
                <Slider
                  value={[settings.passing_percentage]}
                  onValueChange={(v) => setSettings({ ...settings, passing_percentage: v[0] })}
                  min={30}
                  max={80}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Minimum score required to pass the interview. Students scoring below this will fail.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Category Percentage Settings */}
          {questionCategories.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Filter className="w-5 h-5 text-indigo-400" />
                  Question Category Distribution
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Set what percentage of interview questions come from each category. Total must equal 100%.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {questionCategories.map((cat) => {
                  const pct = categoryPercentages[cat] || 0;
                  const questionsFromCat = Math.round(settings.max_interview_questions * pct / 100);
                  return (
                    <div key={cat} className="flex items-center gap-4">
                      <span className="text-sm font-medium min-w-[200px] text-foreground">{cat}</span>
                      <Slider
                        value={[pct]}
                        onValueChange={(v) => {
                          setCategoryPercentages(prev => ({ ...prev, [cat]: v[0] }));
                        }}
                        min={0}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-sm font-bold w-12 text-right text-foreground">{pct}%</span>
                      <span className="text-xs text-muted-foreground w-16 text-right">~{questionsFromCat} Q</span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <span className="text-sm font-medium text-foreground">Total</span>
                  <span className={`text-lg font-bold ${
                    Object.values(categoryPercentages).reduce((a, b) => a + b, 0) === 100
                      ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {Object.values(categoryPercentages).reduce((a, b) => a + b, 0)}%
                    {Object.values(categoryPercentages).reduce((a, b) => a + b, 0) !== 100 && (
                      <span className="text-xs font-normal ml-2">(must equal 100%)</span>
                    )}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const even = Math.floor(100 / questionCategories.length);
                    const remainder = 100 - (even * questionCategories.length);
                    const distributed = {};
                    questionCategories.forEach((c, i) => { distributed[c] = even + (i === 0 ? remainder : 0); });
                    setCategoryPercentages(distributed);
                  }}
                >
                  Distribute Evenly
                </Button>
              </CardContent>
            </Card>
          )}

          {/* AI & Voice Settings */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-purple-400" />
                AI & Voice Settings
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure AI assessment and text-to-speech options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="text-foreground font-medium">Enable AI Assessment</p>
                  <p className="text-xs text-muted-foreground">Automatically score and analyze answers</p>
                </div>
                <Switch
                  checked={settings.enable_ai_assessment}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_ai_assessment: v })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="text-foreground font-medium">Text-to-Speech Questions</p>
                  <p className="text-xs text-muted-foreground">AI reads questions aloud during reading time</p>
                </div>
                <Switch
                  checked={settings.enable_tts_questions}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_tts_questions: v })}
                />
              </div>

              {settings.enable_tts_questions && (
                <div className="space-y-2">
                  <Label className="text-foreground">TTS Voice</Label>
                  <select
                    value={settings.tts_voice}
                    onChange={(e) => setSettings({ ...settings, tts_voice: e.target.value })}
                    className="w-full bg-muted border border-border text-foreground rounded-lg p-2"
                  >
                    <option value="female">Female Voice</option>
                    <option value="male">Male Voice</option>
                  </select>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="text-foreground font-medium">Integrity Checking</p>
                  <p className="text-xs text-muted-foreground">AI analyzes for cheating patterns</p>
                </div>
                <Switch
                  checked={settings.enable_integrity_check}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_integrity_check: v })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Verification Settings */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                Verification Settings
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure identity verification requirements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="text-foreground font-medium">Face Verification</p>
                  <p className="text-xs text-muted-foreground">Match live face with document photo</p>
                </div>
                <Switch
                  checked={settings.enable_face_verification}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_face_verification: v })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="text-foreground font-medium">Liveness Check</p>
                  <p className="text-xs text-muted-foreground">Verify user is a real person</p>
                </div>
                <Switch
                  checked={settings.enable_liveness_check}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_liveness_check: v })}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground">Face Match Threshold</Label>
                  <span className="text-lg font-bold text-green-400">{settings.face_match_threshold}%</span>
                </div>
                <Slider
                  value={[settings.face_match_threshold]}
                  onValueChange={(v) => setSettings({ ...settings, face_match_threshold: v[0] })}
                  min={30}
                  max={95}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Minimum similarity score required to pass face verification
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Recording Settings */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Clock className="w-5 h-5 text-red-400" />
                Recording Settings
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure interview recording options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="text-foreground font-medium">Enable Recording</p>
                  <p className="text-xs text-muted-foreground">Record full interview sessions</p>
                </div>
                <Switch
                  checked={settings.enable_recording}
                  onCheckedChange={(v) => setSettings({ ...settings, enable_recording: v })}
                />
              </div>

              {settings.enable_recording && (
                <div className="space-y-2">
                  <Label className="text-foreground">Recording Quality</Label>
                  <select
                    value={settings.recording_quality}
                    onChange={(e) => setSettings({ ...settings, recording_quality: e.target.value })}
                    className="w-full bg-muted border border-border text-foreground rounded-lg p-2"
                  >
                    <option value="low">Low (480p)</option>
                    <option value="medium">Medium (720p)</option>
                    <option value="high">High (1080p)</option>
                  </select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Failing Criteria Settings */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Failing Criteria
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure automatic interview failure conditions. Interviews will be marked as FAIL if any enabled criteria is violated.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Eye Contact Threshold */}
              <div className="p-4 bg-muted/50 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Eye className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-foreground font-medium">Low Eye Contact</p>
                      <p className="text-xs text-muted-foreground">Fail if cumulative eye contact is below threshold</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.fail_on_low_eye_contact}
                    onCheckedChange={(v) => setSettings({ ...settings, fail_on_low_eye_contact: v })}
                  />
                </div>
                {settings.fail_on_low_eye_contact && (
                  <div className="pl-8 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-foreground text-sm">Threshold</Label>
                      <span className="text-lg font-bold text-red-400">&lt; {settings.eye_contact_threshold}%</span>
                    </div>
                    <Slider
                      value={[settings.eye_contact_threshold]}
                      onValueChange={(v) => setSettings({ ...settings, eye_contact_threshold: v[0] })}
                      min={20}
                      max={80}
                      step={5}
                      className="w-full"
                    />
                  </div>
                )}
              </div>

              {/* Keyword Match Threshold */}
              <div className="p-4 bg-muted/50 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-foreground font-medium">Low Keyword Match</p>
                      <p className="text-xs text-muted-foreground">Fail if average keyword match is below threshold</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.fail_on_low_keyword_match}
                    onCheckedChange={(v) => setSettings({ ...settings, fail_on_low_keyword_match: v })}
                  />
                </div>
                {settings.fail_on_low_keyword_match && (
                  <div className="pl-8 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-foreground text-sm">Threshold</Label>
                      <span className="text-lg font-bold text-red-400">&lt; {settings.keyword_match_threshold}%</span>
                    </div>
                    <Slider
                      value={[settings.keyword_match_threshold]}
                      onValueChange={(v) => setSettings({ ...settings, keyword_match_threshold: v[0] })}
                      min={10}
                      max={60}
                      step={5}
                      className="w-full"
                    />
                  </div>
                )}
              </div>

              {/* Dual Face Detection */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-yellow-400" />
                  <div>
                    <p className="text-foreground font-medium">Multiple Faces Detected</p>
                    <p className="text-xs text-muted-foreground">Fail if additional person detected during interview</p>
                  </div>
                </div>
                <Switch
                  checked={settings.fail_on_dual_face}
                  onCheckedChange={(v) => setSettings({ ...settings, fail_on_dual_face: v })}
                />
              </div>

              {/* Face Absent Detection */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <UserX className="w-5 h-5 text-orange-400" />
                  <div>
                    <p className="text-foreground font-medium">Face Absent</p>
                    <p className="text-xs text-muted-foreground">Fail if student leaves camera view during interview</p>
                  </div>
                </div>
                <Switch
                  checked={settings.fail_on_face_absent}
                  onCheckedChange={(v) => setSettings({ ...settings, fail_on_face_absent: v })}
                />
              </div>

              {/* Face Swap Detection */}
              <div className="p-4 bg-muted/50 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-foreground font-medium">Face Swap / Change Detected</p>
                      <p className="text-xs text-muted-foreground">Fail if face doesn't match the original verified person</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.fail_on_face_swap}
                    onCheckedChange={(v) => setSettings({ ...settings, fail_on_face_swap: v })}
                  />
                </div>
                {settings.fail_on_face_swap && (
                  <div className="pl-8 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-foreground text-sm">Face Match Threshold</Label>
                      <span className="text-lg font-bold text-red-400">&lt; {settings.face_swap_threshold}%</span>
                    </div>
                    <Slider
                      value={[settings.face_swap_threshold]}
                      onValueChange={(v) => setSettings({ ...settings, face_swap_threshold: v[0] })}
                      min={30}
                      max={80}
                      step={5}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Credit Pricing Settings */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Coins className="w-5 h-5 text-amber-500" />
                Credit Pricing Formula
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure interview credit pricing for different user types. 1 Credit = 1 Interview.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Student Pricing */}
              <div className="p-4 bg-muted/50 rounded-xl space-y-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-foreground font-medium">Student Package</p>
                    <p className="text-xs text-muted-foreground">Individual students purchasing credits</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground text-sm">Price per Credit (£)</Label>
                    <Input
                      type="number"
                      value={settings.student_price_per_credit}
                      onChange={(e) => setSettings({ ...settings, student_price_per_credit: parseFloat(e.target.value) || 0 })}
                      className="bg-background border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground text-sm">Minimum Credits</Label>
                    <Input
                      type="number"
                      value={settings.student_min_credits}
                      onChange={(e) => setSettings({ ...settings, student_min_credits: parseInt(e.target.value) || 1 })}
                      className="bg-background border-border"
                    />
                  </div>
                </div>
                <div className="p-2 bg-blue-500/10 rounded text-center">
                  <p className="text-blue-400 text-sm">
                    <PoundSterling className="w-4 h-4 inline" />{settings.student_price_per_credit} × {settings.student_min_credits} credit(s) = 
                    <span className="font-bold ml-1">£{(settings.student_price_per_credit * settings.student_min_credits).toFixed(2)} minimum</span>
                  </p>
                </div>
              </div>

              {/* Agent Pricing */}
              <div className="p-4 bg-muted/50 rounded-xl space-y-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-foreground font-medium">Agent Package</p>
                    <p className="text-xs text-muted-foreground">Immigration agents & consultants</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground text-sm">Price per Credit (£)</Label>
                    <Input
                      type="number"
                      value={settings.agent_price_per_credit}
                      onChange={(e) => setSettings({ ...settings, agent_price_per_credit: parseFloat(e.target.value) || 0 })}
                      className="bg-background border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground text-sm">Minimum Credits</Label>
                    <Input
                      type="number"
                      value={settings.agent_min_credits}
                      onChange={(e) => setSettings({ ...settings, agent_min_credits: parseInt(e.target.value) || 10 })}
                      className="bg-background border-border"
                    />
                  </div>
                </div>
                <div className="p-2 bg-purple-500/10 rounded text-center">
                  <p className="text-purple-400 text-sm">
                    <PoundSterling className="w-4 h-4 inline" />{settings.agent_price_per_credit} × {settings.agent_min_credits} credit(s) = 
                    <span className="font-bold ml-1">£{(settings.agent_price_per_credit * settings.agent_min_credits).toFixed(2)} minimum</span>
                  </p>
                </div>
              </div>

              {/* Organization Pricing */}
              <div className="p-4 bg-muted/50 rounded-xl space-y-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-foreground font-medium">Organization Package</p>
                    <p className="text-xs text-muted-foreground">Universities & large institutions</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground text-sm">Price per Credit (£)</Label>
                    <Input
                      type="number"
                      value={settings.organization_price_per_credit}
                      onChange={(e) => setSettings({ ...settings, organization_price_per_credit: parseFloat(e.target.value) || 0 })}
                      className="bg-background border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground text-sm">Minimum Credits</Label>
                    <Input
                      type="number"
                      value={settings.organization_min_credits}
                      onChange={(e) => setSettings({ ...settings, organization_min_credits: parseInt(e.target.value) || 100 })}
                      className="bg-background border-border"
                    />
                  </div>
                </div>
                <div className="p-2 bg-green-500/10 rounded text-center">
                  <p className="text-green-400 text-sm">
                    <PoundSterling className="w-4 h-4 inline" />{settings.organization_price_per_credit} × {settings.organization_min_credits} credit(s) = 
                    <span className="font-bold ml-1">£{(settings.organization_price_per_credit * settings.organization_min_credits).toFixed(2)} minimum</span>
                  </p>
                </div>
              </div>

              {/* Formula Summary */}
              <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
                <h4 className="text-amber-400 font-medium mb-3 flex items-center gap-2">
                  <Coins className="w-4 h-4" />
                  Credit Formula Summary
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-foreground">
                    <span>Student:</span>
                    <span className="text-foreground font-medium">£{settings.student_price_per_credit}/credit (min {settings.student_min_credits})</span>
                  </div>
                  <div className="flex justify-between text-foreground">
                    <span>Agent:</span>
                    <span className="text-foreground font-medium">£{settings.agent_price_per_credit}/credit (min {settings.agent_min_credits})</span>
                  </div>
                  <div className="flex justify-between text-foreground">
                    <span>Organization:</span>
                    <span className="text-foreground font-medium">£{settings.organization_price_per_credit}/credit (min {settings.organization_min_credits})</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email (SMTP) Settings */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-500" />
                Email Settings (SMTP)
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure SMTP settings to send interview invitation emails to students automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-foreground flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    SMTP Host
                  </Label>
                  <Input
                    value={settings.smtp_host || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    className="bg-muted border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">SMTP Port</Label>
                  <Input
                    type="number"
                    value={settings.smtp_port || 587}
                    onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                    placeholder="587"
                    className="bg-muted border-border text-foreground"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-foreground">SMTP Username</Label>
                  <Input
                    value={settings.smtp_username || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                    placeholder="your-email@gmail.com"
                    className="bg-muted border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">SMTP Password</Label>
                  <Input
                    type="password"
                    value={settings.smtp_password || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                    placeholder="App password or SMTP password"
                    className="bg-muted border-border text-foreground"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-foreground">From Email</Label>
                  <Input
                    type="email"
                    value={settings.smtp_from_email || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                    placeholder="noreply@yourdomain.com"
                    className="bg-muted border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">From Name</Label>
                  <Input
                    value={settings.smtp_from_name || ''}
                    onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                    placeholder="VisaInterviews.ai"
                    className="bg-muted border-border text-foreground"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-foreground font-medium">Use TLS</p>
                    <p className="text-xs text-muted-foreground">Enable secure TLS connection (recommended)</p>
                  </div>
                </div>
                <Switch
                  checked={settings.smtp_use_tls}
                  onCheckedChange={(v) => setSettings({ ...settings, smtp_use_tls: v })}
                />
              </div>

              {/* Test SMTP */}
              <div className="border-t border-border pt-4 space-y-4">
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={testSmtpConnection}
                    disabled={testingSmtp || !settings.smtp_host}
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  >
                    {testingSmtp ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Test Connection
                  </Button>
                </div>
                
                <div className="flex items-center gap-3">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="Enter email to send test"
                    className="flex-1 bg-muted border-border text-foreground"
                  />
                  <Button
                    onClick={sendTestEmailFunc}
                    disabled={testingSmtp || !settings.smtp_host || !testEmail}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    {testingSmtp ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Test Email
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <HelpCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-400 font-medium">How Random Questions Work</p>
                <p className="text-blue-300/80 text-sm mt-1">
                  During each interview, the system will randomly select questions from the question bank. 
                  If you have 100 questions in the bank and set maximum to 10, each student will receive 
                  10 random questions. University-specific questions are prioritized, followed by generic questions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credit Reset Section - Production Handover */}
        <Card className="bg-red-500/10 border-red-500/30">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-red-400">Production Credit Reset</CardTitle>
                <CardDescription className="text-red-300/60">
                  Reset all organization and agent credits to zero for production handover
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* View Credit Summary Button */}
            <Button
              variant="outline"
              onClick={fetchCreditSummary}
              disabled={loadingCredits}
              className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              {loadingCredits ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              View Current Credit Summary
            </Button>

            {/* Credit Summary Display */}
            {creditSummary && (
              <div className="space-y-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <Coins className="w-4 h-4" />
                  Credit Summary
                </h4>
                
                {/* Totals */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-muted-foreground">Organization Credits</p>
                    <p className="text-xl font-bold text-foreground">{creditSummary.totals?.organization_personal_credits || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-muted-foreground">Distributed to Agents</p>
                    <p className="text-xl font-bold text-foreground">{creditSummary.totals?.organization_distributed_credits || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-muted-foreground">Agent Personal</p>
                    <p className="text-xl font-bold text-foreground">{creditSummary.totals?.agent_personal_credits || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-muted-foreground">Agent Allocated</p>
                    <p className="text-xl font-bold text-foreground">{creditSummary.totals?.agent_allocated_credits || 0}</p>
                  </div>
                </div>

                <div className="p-3 bg-red-500/20 rounded-lg border border-red-500/30">
                  <p className="text-muted-foreground">Total Credits to Reset</p>
                  <p className="text-2xl font-bold text-red-400">{creditSummary.totals?.grand_total || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {creditSummary.counts?.organizations || 0} organizations, {creditSummary.counts?.agents || 0} agents
                  </p>
                </div>

                {/* Organization Breakdown */}
                {creditSummary.organizations?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">Organization Breakdown:</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {creditSummary.organizations.map((org, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-slate-800/30 rounded text-sm">
                          <span className="text-foreground truncate">{org.name || org.email}</span>
                          <span className="text-muted-foreground">
                            {org.personal_credits} + {org.distributed_credits} = <strong className="text-foreground">{org.total}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reset Button */}
            {!showResetConfirm ? (
              <Button
                variant="destructive"
                onClick={() => setShowResetConfirm(true)}
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={!creditSummary || creditSummary.totals?.grand_total === 0}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Reset All Credits to Zero
              </Button>
            ) : (
              <div className="space-y-3 p-4 bg-red-500/20 rounded-lg border border-red-500/50">
                <p className="text-red-300 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Are you sure?
                </p>
                <p className="text-red-300/80 text-sm">
                  This will permanently reset ALL credits to zero for all organizations and agents. 
                  This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowResetConfirm(false)}
                    className="flex-1 border-slate-600"
                    disabled={resettingCredits}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={resetAllCredits}
                    disabled={resettingCredits}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    {resettingCredits ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mr-2" />
                    )}
                    Yes, Reset All Credits
                  </Button>
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground text-center">
              Use this when handing over credentials to production clients
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
